/**
 * BoardEditModal — 게시판 수정 다이얼로그
 *
 * 기존 게시판의 이름, 설명, 유형, 디자인 스타일을 수정.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Trans, t } from '@lingui/macro';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useResponsive } from '../../hooks/useResponsive';
import { AdminBadge } from '../components/AdminBadge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { updateBoard, archiveBoard, fetchBoardCategories, setBoardCategory } from '../../services/BoardService';
import {
  Board, BoardCategory, BoardType, DisplayStyle, WritePermission, CommentPermission, MinRole,
  NotifyPermission,
  ALLOWED_TYPES_BY_SCOPE, DEFAULT_STYLE_BY_TYPE,
  WRITE_PERMISSIONS, COMMENT_PERMISSIONS, MIN_ROLES, NOTIFY_PERMISSIONS,
  getWritePermissionOption, getCommentPermissionOption, getMinRoleOption, getNotifyPermissionOption,
  normalizeDisplayStyle,
} from '../../models/Board';
import { useBoardContext } from '../../context/BoardContext';
import { useAppUIContext } from '../../context/AppUIContext';
import { cn } from '../../lib/utils';
import { toast } from '../../components/ui/use-toast';
import { My } from '../../services/My';
import { Fire } from '../../services/FireClient';
import { WorkspaceMember } from '../../models/Workspace';
import { UserAvatar } from '../components/UserAvatar';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  ListFilter, Rows2, LayoutGrid,
  ChevronDown, Shield, ShieldCheck, Trash2,
  Plus, Search, X,
} from 'lucide-react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  board: Board;
}

function useAllBoardTypes(): { value: BoardType; label: string; desc: string }[] {
  return [
    { value: 'general', label: t`General`,    desc: t`Free post sharing` },
    { value: 'notice',  label: t`Notice`,     desc: t`Important announcements` },
    { value: 'qna',     label: t`Q&A`,        desc: t`Questions and answers` },
    // 이슈 추적은 신규 생성에서는 비표시. 기존 issue 게시판 편집 시에는 allowedTypes 가 board.type 을 보호.
    { value: 'issue',   label: t`Issue tracking`, desc: t`Lightweight task tracking` },
    { value: 'wiki',    label: t`Wiki`,       desc: t`Document organization` },
  ];
}

// 보기 스타일 picker — 관리자만 변경 가능. 설정 모달 자체가 관리자만 진입하므로 표시 OK.
function useDisplayStyles(): { value: DisplayStyle; label: string; icon: React.ElementType; desc: string }[] {
  return [
    { value: 'notion',    label: t`Table`,           icon: ListFilter, desc: t`PC-optimized table — title/author/tags/date/comments/views` },
    { value: 'basecamp',  label: t`Card (grid)`,     icon: LayoutGrid, desc: t`Show first image as thumbnail; otherwise body preview` },
    { value: 'discourse', label: t`Forum`,           icon: Rows2,      desc: t`Left thumbnail + body preview if image is present` },
  ];
}

const BoardEditModal: React.FC<Props> = ({ open, onOpenChange, board }) => {
  const { refreshBoards, setSelectedBoard } = useBoardContext();
  const { rooms } = useAppUIContext();
  const { isMobile } = useResponsive();
  const [name, setName] = useState(board.name);
  const [description, setDescription] = useState(board.description ?? '');
  const [type, setType] = useState<BoardType>(board.type);
  const [displayStyle, setDisplayStyle] = useState<DisplayStyle>(
    normalizeDisplayStyle(board.display_style as string | null) ?? DEFAULT_STYLE_BY_TYPE[board.type]
  );
  const [writePermission, setWritePermission] = useState<WritePermission>(board.write_permission);
  const [commentPermission, setCommentPermission] = useState<CommentPermission>(board.comment_permission);
  const [notifyAllPermission, setNotifyAllPermission] = useState<NotifyPermission>(board.notify_all_permission);
  const [minRole, setMinRole] = useState<MinRole>(board.min_role ?? 'member');
  const [adminIds, setAdminIds] = useState<string[]>(board.admin_ids ?? []);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // 카테고리 picker — 워크스페이스 스코프 보드만 의미 있음
  const [categoryId, setCategoryId] = useState<string | null>(board.category_id ?? null);
  const [categories, setCategories] = useState<BoardCategory[]>([]);
  useEffect(() => {
    if (!open || board.scope !== 'workspace') return;
    fetchBoardCategories(board.workspace_id).then(setCategories).catch(() => {});
  }, [open, board.scope, board.workspace_id]);
  useEffect(() => { setCategoryId(board.category_id ?? null); }, [board.id, board.category_id]);
  /** 게시판 삭제(아카이브) 확인 다이얼로그 — 자주 쓰는 동작이 아니라 설정 안에 숨김 */
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archiving, setArchiving] = useState(false);

  /** 게시판 아카이브(소프트 삭제) — 모달 닫고 보드 컨텍스트 새로고침 */
  const handleArchive = async () => {
    if (archiving) return;
    const boardName = board.name;
    setArchiving(true);
    try {
      await archiveBoard(board.id);
      await refreshBoards();
      setSelectedBoard(null);
      setShowArchiveConfirm(false);
      onOpenChange(false);
      toast({ title: t`Board "${boardName}" deleted` });
    } catch (err) {
      console.error('[BoardEdit] 게시판 아카이브 실패:', err);
      toast({ title: t`Failed to delete`, variant: 'destructive' });
    } finally {
      setArchiving(false);
    }
  };

  const isChannel = board.scope === 'channel';

  // 채널 게시판일 때 관리자 후보 — 해당 채널 멤버
  const channelMembers = useMemo(() => {
    if (!isChannel || !board.channel_id) return [];
    const room = rooms.find(r => r.id === board.channel_id);
    return room?.users ?? [];
  }, [isChannel, board.channel_id, rooms]);

  // 워크스페이스 보드일 때 관리자 후보 — 워크스페이스 멤버 (member role 만, owner/admin 은 자동 권한이라 제외)
  const [workspaceMembers, setWorkspaceMembers] = useState<WorkspaceMember[]>([]);
  const [adminPickerOpen, setAdminPickerOpen] = useState(false);
  const [adminPickerQuery, setAdminPickerQuery] = useState('');
  useEffect(() => {
    if (!open || isChannel) return;
    Fire.ws.getWorkspaceMembers(board.workspace_id).then(setWorkspaceMembers).catch(() => {});
  }, [open, isChannel, board.workspace_id]);
  const workspaceAdminCandidates = useMemo(() => {
    const draftSet = new Set(adminIds);
    const q = adminPickerQuery.trim().toLowerCase();
    return workspaceMembers
      .filter(m => m.role === 'member')
      .filter(m => !draftSet.has(m.id))
      .filter(m => !q || m.user.name.toLowerCase().includes(q) || m.user.email?.toLowerCase().includes(q));
  }, [workspaceMembers, adminIds, adminPickerQuery]);
  const designatedAdminMembers = useMemo(() => {
    return adminIds
      .map(id => workspaceMembers.find(m => m.id === id))
      .filter((m): m is WorkspaceMember => !!m);
  }, [adminIds, workspaceMembers]);

  // board prop 변경 시 초기값 동기화
  useEffect(() => {
    setName(board.name);
    setDescription(board.description ?? '');
    setType(board.type);
    setDisplayStyle(normalizeDisplayStyle(board.display_style as string | null) ?? DEFAULT_STYLE_BY_TYPE[board.type]);
    setWritePermission(board.write_permission);
    setCommentPermission(board.comment_permission);
    setNotifyAllPermission(board.notify_all_permission);
    setMinRole(board.min_role ?? 'member');
    setAdminIds(board.admin_ids ?? []);
  }, [board.id]);

  const allBoardTypes = useAllBoardTypes();
  const displayStyles = useDisplayStyles();
  // 스코프별 허용 유형. 기존 board.type 이 ALLOWED 에서 빠졌더라도(예: issue) select 에는 표시 — 데이터 안전.
  const allowedTypes = useMemo(() => {
    const baseAllowed = ALLOWED_TYPES_BY_SCOPE[board.scope];
    return allBoardTypes.filter(bt =>
      baseAllowed.includes(bt.value) || bt.value === board.type
    );
  }, [board.scope, board.type, allBoardTypes]);

  const adminIdsChanged =
    adminIds.length !== (board.admin_ids ?? []).length
    || adminIds.some(id => !board.admin_ids?.includes(id));
  const categoryChanged = (categoryId ?? null) !== (board.category_id ?? null);
  const hasChanges = name !== board.name
    || description !== (board.description ?? '')
    || type !== board.type
    || displayStyle !== (normalizeDisplayStyle(board.display_style as string | null) ?? DEFAULT_STYLE_BY_TYPE[board.type])
    || writePermission !== board.write_permission
    || commentPermission !== board.comment_permission
    || notifyAllPermission !== board.notify_all_permission
    || minRole !== (board.min_role ?? 'member')
    || adminIdsChanged
    || categoryChanged;

  const handleSubmit = async () => {
    if (!name.trim() || submitting) return;
    setSubmitting(true);
    try {
      // 채널 게시판은 권한이 "채널 멤버 누구나" 로 고정
      const effectiveWritePerm = isChannel ? 'members' : writePermission;
      const effectiveCommentPerm = isChannel ? 'members' : commentPermission;
      const effectiveMinRole: MinRole = isChannel ? 'member' : minRole;
      const updated = await updateBoard(board.id, {
        name: name.trim(),
        description: description.trim() || undefined,
        type,
        display_style: displayStyle,
        admin_ids: adminIds,
        write_permission: effectiveWritePerm,
        comment_permission: effectiveCommentPerm,
        notify_all_permission: notifyAllPermission,
        min_role: effectiveMinRole,
      });
      // 카테고리 변경 — 별도 트랜잭션 (sort_order 자동 재계산)
      if (categoryChanged) {
        await setBoardCategory(board.id, categoryId);
      }
      onOpenChange(false);
      await refreshBoards();
      setSelectedBoard(updated);
      toast({ title: t`Board updated` });
    } catch (err) {
      console.error('[BoardEdit] 게시판 수정 실패:', err);
      toast({ title: t`Failed to update board`, description: (err as Error).message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px] max-h-[85vh] flex flex-col gap-0">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Trans>Edit board</Trans>
            <AdminBadge variant="sm" />
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-5 mt-2 -mr-6 pr-6">
          {/* 이름 */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <Trans>Board name</Trans>
            </label>
            <Input
              placeholder={t`Board name`}
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
            />
          </div>

          {/* 설명 */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <Trans>Description</Trans>
            </label>
            <Input
              placeholder={t`Description (optional)`}
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </div>

          {/* 카테고리 — 워크스페이스 보드만. 카테고리 없으면 안내 */}
          {board.scope === 'workspace' && (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <Trans>Category</Trans>
              </label>
              {categories.length === 0 ? (
                <p className="text-meta-sm text-muted-foreground px-3 py-2 rounded-lg bg-muted">
                  <Trans>No categories. Add them from ⚙ Manage categories in the left menu.</Trans>
                </p>
              ) : (
                <Select
                  value={categoryId ?? '__none__'}
                  onValueChange={v => setCategoryId(v === '__none__' ? null : v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">
                      <span className="text-muted-foreground"><Trans>— Uncategorized —</Trans></span>
                    </SelectItem>
                    {categories.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* 스코프 (읽기전용 표시) */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <Trans>Scope</Trans>
            </label>
            <div className="text-sm px-3 py-2 rounded-lg bg-muted text-muted-foreground">
              {board.scope === 'workspace' ? <Trans>📦 Workspace</Trans> : board.scope === 'channel' ? <Trans># Channel</Trans> : <Trans>👤 Personal</Trans>}
            </div>
          </div>

          {/* 유형 */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <Trans>Board type</Trans>
            </label>
            <Select value={type} onValueChange={v => {
              const newType = v as BoardType;
              setType(newType);
              setDisplayStyle(DEFAULT_STYLE_BY_TYPE[newType]);
            }}>
              <SelectTrigger>
                <SelectValue placeholder={t`Board type`} />
              </SelectTrigger>
              <SelectContent>
                {allowedTypes.map(bt => (
                  <SelectItem key={bt.value} value={bt.value}>
                    <div className="flex items-center gap-2">
                      <span>{bt.label}</span>
                      <span className="text-meta-sm text-muted-foreground">{bt.desc}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 디자인 스타일 — 관리자 전용. wiki 는 type 고정이라 숨김. 모바일은 포럼형 강제라 picker 숨기고 안내만 표시. */}
          {type !== 'wiki' && !isMobile && (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <Trans>Display style</Trans>
              </label>
              <div className="grid grid-cols-3 gap-1.5">
                {displayStyles.map(style => {
                  const Icon = style.icon;
                  const isActive = displayStyle === style.value;
                  return (
                    <button
                      key={style.value}
                      onClick={() => setDisplayStyle(style.value)}
                      className={cn(
                        'flex flex-col items-center gap-1.5 py-3 px-2 rounded-lg border transition-all duration-200 cursor-pointer',
                        isActive
                          ? 'border-primary bg-primary/8 text-primary'
                          : 'border-transparent hover:bg-muted text-muted-foreground hover:text-foreground',
                      )}
                    >
                      <Icon className={cn('size-5', isActive && 'text-primary')} strokeWidth={1.5} />
                      <span className="text-meta-sm font-medium leading-tight text-center">{style.label}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-meta-sm text-muted-foreground pl-0.5">
                {displayStyles.find(s => s.value === displayStyle)?.desc}
              </p>
            </div>
          )}

          {/* 모바일 — picker 숨기고 포럼 고정 안내만 (모바일은 테이블형이 포럼형으로 강제됨) */}
          {type !== 'wiki' && isMobile && (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <Trans>Display style</Trans>
              </label>
              <p className="text-meta-sm text-muted-foreground pl-0.5">
                <Trans>Display style is set on a wider screen. Mobile always shows the forum view.</Trans>
              </p>
            </div>
          )}

          {/* ── 채널 게시판 전용: 관리자 선택 (채널 멤버 중에서) ── */}
          {isChannel && (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <ShieldCheck className="size-3.5" />
                <Trans>Admins</Trans>
              </label>
              <p className="text-meta-sm text-muted-foreground -mt-1">
                <Trans>Any channel member can read/write. You can also designate admins.</Trans>
              </p>
              {channelMembers.length === 0 ? (
                <div className="px-3 py-2 text-xs text-muted-foreground bg-muted/50 rounded-lg">
                  <Trans>No channel members</Trans>
                </div>
              ) : (
                <div className="max-h-40 overflow-y-auto border border-border rounded-lg divide-y divide-border">
                  {channelMembers.map(u => {
                    const isOwner = u.id === board.owner_id;
                    const checked = isOwner || adminIds.includes(u.id);
                    return (
                      <label
                        key={u.id}
                        className={cn(
                          'flex items-center gap-2.5 px-2.5 py-2 text-sm transition-colors',
                          isOwner
                            ? 'opacity-70 cursor-not-allowed'
                            : 'hover:bg-muted/60 cursor-pointer',
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={isOwner}
                          onChange={e => {
                            if (e.target.checked) {
                              setAdminIds(prev => Array.from(new Set([...prev, u.id])));
                            } else {
                              setAdminIds(prev => prev.filter(id => id !== u.id));
                            }
                          }}
                          className="size-4 rounded border-border"
                        />
                        <UserAvatar src={u.photoUrl} name={u.name} className="size-6" />
                        <span className="flex-1 truncate">{u.name}</span>
                        {isOwner && (
                          <span className="text-meta-sm text-muted-foreground"><Trans>Board owner</Trans></span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── 워크스페이스 게시판: 게시판 관리자 위임 (멤버 picker) ── */}
          {!isChannel && (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <ShieldCheck className="size-3.5" />
                <Trans>Board admins</Trans>
              </label>
              <p className="text-meta-sm text-muted-foreground -mt-1 leading-relaxed">
                <Trans>Select members to delegate as board admins. Workspace owner/admin always have permission automatically.</Trans>
              </p>

              {/* 현재 명단 — chip 리스트 */}
              {designatedAdminMembers.length > 0 ? (
                <ul className="flex flex-wrap gap-2">
                  {designatedAdminMembers.map(m => (
                    <li
                      key={m.id}
                      className={cn(
                        'inline-flex items-center gap-2 pl-1.5 pr-1 py-1 rounded-full bg-muted/60 border border-border',
                        m.role !== 'member' && 'opacity-60',
                      )}
                      title={m.role !== 'member' ? t`Already has ${m.role} permission — delegation is void` : undefined}
                    >
                      <UserAvatar src={m.user.photoUrl} name={m.user.name} className="size-5 shrink-0" />
                      <span className="text-meta-sm font-medium text-foreground">{m.user.name}</span>
                      <button
                        type="button"
                        onClick={() => setAdminIds(prev => prev.filter(id => id !== m.id))}
                        className="size-5 rounded-full flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors cursor-pointer"
                        title={t`Remove`}
                        aria-label={t`Remove ${m.user.name}`}
                      >
                        <X className="size-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}

              {/* 추가 picker */}
              {!adminPickerOpen ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setAdminPickerOpen(true)}
                  className="gap-1.5"
                >
                  <Plus className="size-3.5" />
                  <Trans>Add member</Trans>
                </Button>
              ) : (
                <div className="rounded-lg border border-border bg-background p-2 space-y-1.5">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
                    <Input
                      autoFocus
                      value={adminPickerQuery}
                      onChange={e => setAdminPickerQuery(e.target.value)}
                      placeholder={t`Search by name or email…`}
                      className="pl-8 h-8 text-meta"
                    />
                    <button
                      type="button"
                      onClick={() => { setAdminPickerOpen(false); setAdminPickerQuery(''); }}
                      className="absolute right-1 top-1/2 -translate-y-1/2 size-6 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                    >
                      <X className="size-3" />
                    </button>
                  </div>
                  <div className="max-h-[180px] overflow-y-auto -mx-1 px-1">
                    {workspaceAdminCandidates.length === 0 ? (
                      <p className="text-meta-sm text-muted-foreground text-center py-3">
                        {workspaceMembers.filter(m => m.role === 'member').length === 0
                          ? <Trans>No members in the workspace.</Trans>
                          : <Trans>No search results.</Trans>}
                      </p>
                    ) : (
                      <ul className="space-y-0.5">
                        {workspaceAdminCandidates.map(m => (
                          <li key={m.id}>
                            <button
                              type="button"
                              onClick={() => {
                                setAdminIds(prev => [...prev, m.id]);
                                setAdminPickerOpen(false);
                                setAdminPickerQuery('');
                              }}
                              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted text-left cursor-pointer transition-colors"
                            >
                              <UserAvatar src={m.user.photoUrl} name={m.user.name} className="size-6 shrink-0" />
                              <span className="text-label text-foreground truncate">{m.user.name}</span>
                              {m.user.email && (
                                <span className="text-meta-sm text-muted-foreground truncate ml-auto">{m.user.email}</span>
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── 권한 설정 (접기/펼치기) — 채널 스코프는 권한 고정이므로 숨김 ── */}
          {!isChannel && (
          <div className="border border-border rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors cursor-pointer"
            >
              <Shield className="size-4" />
              <span><Trans>Permissions</Trans></span>
              <ChevronDown className={cn('size-3.5 ml-auto transition-transform duration-200', showAdvanced && 'rotate-180')} />
            </button>
            {showAdvanced && (
              <div className="px-3 pb-3 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200 border-t border-border pt-3">
                <div className="space-y-1.5">
                  <label className="text-meta-sm font-semibold text-muted-foreground"><Trans>Post write</Trans></label>
                  <Select value={writePermission} onValueChange={v => setWritePermission(v as WritePermission)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {WRITE_PERMISSIONS.map(k => {
                        const o = getWritePermissionOption(k);
                        return (
                        <SelectItem key={k} value={k}>
                          <span>{o.label}</span>
                          <span className="text-meta-sm text-muted-foreground ml-1.5">{o.desc}</span>
                        </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-meta-sm font-semibold text-muted-foreground"><Trans>Comment write</Trans></label>
                  <Select value={commentPermission} onValueChange={v => setCommentPermission(v as CommentPermission)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {COMMENT_PERMISSIONS.map(k => {
                        const o = getCommentPermissionOption(k);
                        return (
                        <SelectItem key={k} value={k}>
                          <span>{o.label}</span>
                          <span className="text-meta-sm text-muted-foreground ml-1.5">{o.desc}</span>
                        </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                {/* @개인 멘션은 작성권한과 자동 일치 — 별도 권한 노출 안 함 */}
                <div className="space-y-1.5">
                  <label className="text-meta-sm font-semibold text-muted-foreground"><Trans>@all mention</Trans></label>
                  <Select value={notifyAllPermission} onValueChange={v => setNotifyAllPermission(v as NotifyPermission)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {NOTIFY_PERMISSIONS.map(k => {
                        const o = getNotifyPermissionOption(k);
                        return (
                        <SelectItem key={k} value={k}>
                          <span>{o.label}</span>
                          <span className="text-meta-sm text-muted-foreground ml-1.5">{o.desc}</span>
                        </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-meta-sm font-semibold text-muted-foreground"><Trans>Guest access</Trans></label>
                  <Select value={minRole} onValueChange={v => setMinRole(v as MinRole)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MIN_ROLES.map(k => {
                        const o = getMinRoleOption(k);
                        return (
                        <SelectItem key={k} value={k}>
                          <span>{o.label}</span>
                          <span className="text-meta-sm text-muted-foreground ml-1.5">{o.desc}</span>
                        </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
          )}

          {/* 액션 */}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              <Trans>Cancel</Trans>
            </Button>
            <Button onClick={handleSubmit} disabled={!name.trim() || submitting || !hasChanges}>
              {submitting ? <Trans>Saving…</Trans> : <Trans>Save</Trans>}
            </Button>
          </div>

          {/* ── 게시판 삭제 — 극히 드물게 쓰는 동작이라 액션 하단에 약하게 노출 ── */}
          <div className="flex items-center justify-end pt-3 mt-1 border-t border-border/40">
            <button
              type="button"
              onClick={() => setShowArchiveConfirm(true)}
              className="inline-flex items-center gap-1 text-meta-sm text-muted-foreground/70 hover:text-destructive transition-colors"
            >
              <Trash2 className="size-3" />
              <span><Trans>Delete board</Trans></span>
            </button>
          </div>
        </div>
      </DialogContent>

      {/* 삭제 확인 다이얼로그 */}
      <AlertDialog open={showArchiveConfirm} onOpenChange={setShowArchiveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t`Delete board "${board.name}"?`}</AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>It will disappear from the board list and all posts will no longer be visible.</Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiving}><Trans>Cancel</Trans></AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchive}
              disabled={archiving}
              className="bg-destructive hover:bg-destructive/90"
            >
              {archiving ? <Trans>Deleting…</Trans> : <Trans>Delete</Trans>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

export default BoardEditModal;
